// Seeded reference codes (0004_seed_defaults). Ticket statuses/priorities are
// tenant-configurable DB rows served by /api/config/statuses|priorities; these
// constants are only the FALLBACK shown while those lookups load (or fail).

import type { TicketPriorityCode, TicketStatusCode } from "./types";

export const TICKET_STATUSES: { code: TicketStatusCode; label: string }[] = [
  { code: "open", label: "Open" },
  { code: "pending", label: "Pending" },
  { code: "solved", label: "Solved" },
  { code: "closed", label: "Closed" },
];

export const TICKET_PRIORITIES: { code: TicketPriorityCode; label: string }[] = [
  { code: "low", label: "Low" },
  { code: "normal", label: "Normal" },
  { code: "high", label: "High" },
  { code: "urgent", label: "Urgent" },
];

/** Task statuses also seeded; reused by the task drawer editor. */
export const TASK_STATUSES: { code: string; label: string }[] = [
  { code: "todo", label: "To do" },
  { code: "in_progress", label: "In progress" },
  { code: "done", label: "Done" },
];

export const TASK_PRIORITIES = TICKET_PRIORITIES;

// ----- Project portfolio metadata (0016) -----------------------------------

/** Health status of a project, with the CSS modifier used for its pill color. */
export const PROJECT_STATUSES: { code: string; label: string; tone: string }[] = [
  { code: "not_started", label: "Not started", tone: "neutral" },
  { code: "on_track", label: "On track", tone: "ok" },
  { code: "at_risk", label: "At risk", tone: "warn" },
  { code: "delayed", label: "Delayed", tone: "danger" },
  { code: "on_hold", label: "On hold", tone: "info" },
  { code: "done", label: "Done", tone: "done" },
];

export const PROJECT_PRIORITIES: { code: string; label: string; tone: string }[] = [
  { code: "low", label: "Low", tone: "ok" },
  { code: "medium", label: "Medium", tone: "warn" },
  { code: "high", label: "High", tone: "danger" },
  { code: "urgent", label: "Urgent", tone: "danger" },
];

/** Emoji icon presets for the project picker (also accepts a freely-typed one). */
export const PROJECT_ICONS = [
  "📁", "🚀", "🛠️", "💡", "📊", "🎯", "🧩", "🌐", "🔒", "📱", "🤖", "⚙️", "📨", "🧪", "🏗️", "🔧",
];

/** Accent color presets for project icons (Tailwind-ish palette). */
export const PROJECT_COLORS = [
  "#6366f1", "#22c55e", "#eab308", "#3b82f6", "#ec4899", "#f97316", "#14b8a6", "#a855f7",
];

export const projectStatusMeta = (code?: string) =>
  PROJECT_STATUSES.find((s) => s.code === code) ?? PROJECT_STATUSES[1]!;
export const projectPriorityMeta = (code?: string) =>
  PROJECT_PRIORITIES.find((p) => p.code === code) ?? PROJECT_PRIORITIES[1]!;

// ----- Facilities module (building-management request queue) ---------------
// Fixed value sets for the facility request queue (validated server-side in
// workers/ac-edge/src/index.ts). Unlike ticket statuses/priorities these are
// NOT tenant-configurable DB rows — they are constants for this module.

export const FACILITY_CATEGORIES: { code: string; label: string }[] = [
  { code: "hvac", label: "HVAC" },
  { code: "plumbing", label: "Plumbing" },
  { code: "electrical", label: "Electrical" },
  { code: "cleaning", label: "Cleaning" },
  { code: "safety", label: "Safety" },
  { code: "grounds", label: "Grounds" },
  { code: "other", label: "Other" },
];

export const FACILITY_STATUSES: { code: string; label: string }[] = [
  { code: "new", label: "New" },
  { code: "in_progress", label: "In progress" },
  { code: "on_hold", label: "On hold" },
  { code: "done", label: "Done" },
  { code: "cancelled", label: "Cancelled" },
];

export const FACILITY_PRIORITIES: { code: string; label: string }[] = [
  { code: "low", label: "Low" },
  { code: "normal", label: "Normal" },
  { code: "high", label: "High" },
  { code: "urgent", label: "Urgent" },
];

// 0039 CMMS value sets — work-order types, preventive-maintenance recurrence
// cadences, and equipment-registry asset statuses.
export const FACILITY_WO_TYPES: { code: string; label: string }[] = [
  { code: "reactive", label: "Reactive" },
  { code: "preventive", label: "Preventive" },
  { code: "inspection", label: "Inspection" },
];

export const FACILITY_RECURRENCES: { code: string; label: string }[] = [
  { code: "none", label: "None" },
  { code: "daily", label: "Daily" },
  { code: "weekly", label: "Weekly" },
  { code: "monthly", label: "Monthly" },
  { code: "quarterly", label: "Quarterly" },
  { code: "yearly", label: "Yearly" },
];

export const FACILITY_ASSET_STATUSES: { code: string; label: string }[] = [
  { code: "operational", label: "Operational" },
  { code: "down", label: "Down" },
  { code: "maintenance", label: "Maintenance" },
  { code: "retired", label: "Retired" },
];

// 0040: units of measure for inventory parts.
export const FACILITY_PART_UNITS: { code: string; label: string }[] = [
  { code: "each", label: "Each" },
  { code: "box", label: "Box" },
  { code: "ft", label: "Feet" },
  { code: "gal", label: "Gallon" },
  { code: "lb", label: "Pound" },
  { code: "pair", label: "Pair" },
  { code: "roll", label: "Roll" },
];
